import { ReactElement, useState } from "react";
import { IProcessInformation } from "./types";
import {
  Box,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from "@mui/material";
import StyledTableRow from "../../../components/styledTableRow";
import { IColumn, Order } from "../../../types";
import { formatDateTimeWithSecondsX, getComparator, stableSort } from "../../../system/utils";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DateTime } from "luxon";

interface IProps {
  processes: IProcessInformation[];
}

interface IRowProps {
  process: IProcessInformation;
}

const columns: IColumn[] = [
  {
    id: "process",
    label: "Process",
    minWidth: 200
  },
  {
    id: "host",
    label: "Host",
    minWidth: 200
  },
  {
    id: "datetime",
    label: "Datetime",
    minWidth: 200,
  }
];

const thirtySeconds = 30;
const oneMinute = 60;


const RowComponent = ({ process }: IRowProps) => {
  const [open, setOpen] = useState(false);
  let colorDate: "error" | "success" | "warning" = "success";

  const deltaDatetime = DateTime.now().toSeconds() - process.datetime;
  if (deltaDatetime > oneMinute) {
    colorDate = "error";
  } else if (deltaDatetime > thirtySeconds) {
    colorDate = "warning";
  }

  return (
    <>
      <StyledTableRow hover={true} tabIndex={-1}  >
        <TableCell className="table-cell" >
          <div className="display-flex-row">
            { process.logs.length > 0 &&
              <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
              </IconButton>
            }
          </div>
        </TableCell>
        <TableCell>
          {process.process}
        </TableCell>
        <TableCell>
          {process.host}
        </TableCell>
        <TableCell>
          <Chip color={colorDate} label={formatDateTimeWithSecondsX(process.datetime)} size="small" />
        </TableCell>
      </StyledTableRow>
      { process.logs.length > 0 &&
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: "none" }} colSpan={4} className="table-cell">
            <Collapse in={open} timeout="auto" unmountOnExit={true}>
              <Box sx={{ m: 1, background: "rgba(127, 127, 127, .2)" }}>
                <Typography variant="h6" gutterBottom={true} component="div">
                  Logs
                </Typography>
                <Table size="small" stickyHeader={true}  aria-label="machines">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ backgroundColor: "#b0bec5" }}>Datetime</TableCell>
                      <TableCell style={{ backgroundColor: "#b0bec5" }}>level</TableCell>
                      <TableCell style={{ backgroundColor: "#b0bec5" }}>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    { process.logs.map((log, index) => {
                      let color:  "error" | "info" | "warning" = "info";

                      if (log.level === "error") {
                        color = "error";
                      } else if (log.level === "warning") {
                        color = "warning";
                      }

                      return (
                        <TableRow key={`log-${index}`}>
                          <TableCell>{formatDateTimeWithSecondsX(log.datetime)}</TableCell>
                          <TableCell><Chip color={color} label={log.level} size="small" /></TableCell>
                          <TableCell>{log.message}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      }
    </>
  );
};

const ProcessesComponent = ({ processes }: IProps): ReactElement => {
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof IProcessInformation>("process");

  const createSortHandler = (property: keyof IProcessInformation) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <div className="display-flex-column flex-grow-0 jus">
      <strong>Containers</strong>
      <Table stickyHeader={true}  size={"small"} className="justify-self-stretch">
        <TableHead>
          <TableRow>
            <TableCell style={{ backgroundColor: "#b0bec5",  minWidth: 30 }} />
            {columns.map((column) => {
              return (
                <TableCell
                  key={`${column.id}`}
                  style={{ backgroundColor: "#b0bec5", minWidth: column.minWidth }}>

                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={createSortHandler(column.id as keyof IProcessInformation)}>
                    {column.label}
                    {orderBy === column.id
                      ? <span className="cell-visuallyHidden">
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </span>
                      : null}
                  </TableSortLabel>
                </TableCell>
              );
            })}
            <TableCell style={{ backgroundColor: "#b0bec5", minWidth: 60 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {stableSort<IProcessInformation>(processes, getComparator(order, orderBy))
            .map((process, index) => {

              return (
                <RowComponent process={process} key={`process-${index}`}/>
              );
            })}
        </TableBody>
      </Table>
    </div>
  );
};

export default ProcessesComponent;